import React, { useEffect, useState } from 'react';
import api from '../../common/api';
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import makeStyles from "@material-ui/styles/makeStyles";
import { ConfirmDialog, MaterialTable } from '../../components';
import useTheme from '@material-ui/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MedicineStock from '../MedicineStock/components/medicineStocks';
import Icon from '@material-ui/core/Icon';
import moment from 'moment';
import Mobile from "./components/movementMobile";
import SearchIcon from '@material-ui/icons/Search';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import {
    MuiPickersUtilsProvider,
    KeyboardDatePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles(theme => ({
    root: props => ({
        margin: props.isDesktop ? theme.spacing(1) : theme.spacing(0.5)
    }),
    // Description on the left, Move Stock on the right (wraps on small screens)
    topBar: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5)
    },
}));

// Feed (materialTypeId 5) lives in its own ledger/routes; Medicine and
// Vaccination share the medicine-stock routes. This single screen merges
// both so a user picks the material type inside the dialog instead of
// switching pages.
const FEED_TYPE_ID = 5;
const isFeed = row =>
    row && (row.materialTypeId === FEED_TYPE_ID || row.materialType === 'Feed');
const stockApiFor = row => (isFeed(row) ? 'feed-stock' : 'medicine-stock');
// Rows from the two ledgers can share numeric ids, so identity needs the type.
const sameRow = (a, b) =>
    a && b && a.id === b.id && a.materialType === b.materialType;

let currentStockData = undefined;
let editMode = false;

const StockMovement = ({ showError, showMessage, history, match }) => {

    const [stockData, setStockData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterSelectedStartDate, setFilterSelectedStartDate] = useState(null);
    const [filterSelectedEndDate, setFilterSelectedEndDate] = useState(null);
    const [showStockDialog, setShowStockDialog] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [Shed, setShed] = useState([]);
    const [farms, setFarms] = useState([]);
    const [MaterialList, setMaterialList] = useState(null);
    const [MaterialTypeList, setMaterialTypeList] = useState(null);
    const [MaterialType, setMaterialType] = useState(null);
    const theme = useTheme();

    const handleFilterStartDateChange = date => setFilterSelectedStartDate(date);
    const handleFilterEndDateChange = date => setFilterSelectedEndDate(date);

    const isDesktop = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true
    });
    const classes = useStyles({ isDesktop });

    function onNewStock(event) {
        editMode = false;
        setErrorMessage("");
        event.preventDefault();
        setShowStockDialog(true);
    }

    function handleStockClose() {
        setErrorMessage(null);
        currentStockData = undefined;
        setShowStockDialog(false);
    }

    function handleDeleteConfirmClose() {
        setShowDeleteConfirm(false);
    }

    function handleMaterialTypeChange(event) {
        setMaterialType(MaterialTypeList[event.target.value]);
    }

    //Get all farms API Call
    const getAllFarms = () => {
        showError('');
        showMessage('');
        const response = api.get('farm');
        response.then(res => {
            setFarms([...res]);
        }).catch(err => {
            if (!(err.message === 'Bad Request' || err.message === 'No data found'))
                console.log(err.message || 'Unknown Error');
        });
        return response;
    };

    function getAllSheds(farmID) {
        setLoading(true);
        showMessage('');
        const response = api.get(`farm/${farmID}/shed`);
        response.then(res => {
            setShed(res);
        }).catch(err => {
            if (err.message) {
                if (err.message === 'Bad Request' || err.message === 'No data found')
                    setErrorMessage('No Sheds');
                else
                    setErrorMessage(err.message);
            } else {
                setErrorMessage('Unknown Error');
            }
        }).finally(() => {
            setLoading(false);
        });
        return response;
    }

    const onStockDeleteConfirm = () => {
        if (currentStockData && currentStockData.id) {
            const target = currentStockData;
            setShowDeleteConfirm(false);
            setLoading(true);
            showError('');
            const response = api.delete(`${stockApiFor(target)}/delete/${target.id}/${match.params["farmID"]}`);
            response.then(res => {
                if (res) {
                    setStockData(prev => prev.filter(row => !sameRow(row, target)));
                }
            }).catch(err => {
                if (err.message) {
                    if (err.message === 'Bad Request' || err.message === 'No data found')
                        showError('Stock not Deleted');
                    else
                        showError(err.message);
                } else {
                    showError('Unknown Error');
                }
            }).finally(() => {
                setLoading(false);
                currentStockData = undefined;
            });
        }
    };

    const removeErrorMessage = () => setErrorMessage('');

    const onStockDataDelete = (rowData) => {
        currentStockData = rowData;
        setShowDeleteConfirm(true);
    };

    const onStockSave = (StockData, setpopuploading) => {
        showMessage('');
        const response = api.post(`${stockApiFor(StockData)}/create`, StockData);
        response.then(res => {
            let materials = MaterialList[res.materialType];
            let found = materials && materials.find((el) => el.materialMasterId === res.materialMasterId);
            if (res.isStorage === 0) {
                let shedMatch = Shed.find((el) => el.id === res.shedId);
                res.shedName = shedMatch ? shedMatch.name : res.shedName;
            } else {
                let farmName = farms.find((farm) => farm.id === res.farmId)?.name;
                let transferFarmName = farms.find((farm) => farm.id === res.transferFarmID)?.name;
                res.shedName = farmName + "→" + transferFarmName + "(Storage)";
            }
            res.materialName = found ? found.materialName : res.materialName;
            res.materialType = StockData.materialType;
            setStockData(prev => [...prev, res]);
            setShowStockDialog(false);
        }).catch(err => {
            if (err.message) {
                if (err.message === 'Bad Request' || err.message === 'No data found') {
                    setErrorMessage('Stock not added');
                } else if (err.message === 'duplicate key') {
                    setErrorMessage("Duplicate entry. Stock already exists");
                } else if (err.message === 'no batch found') {
                    setErrorMessage(`No batch found for shed ${StockData.shedName} on ${moment(StockData.date).format("DD-MM-YYYY")}`);
                } else if (err.message === 'no stock') {
                    setErrorMessage('Material with available stock 0 cannot be added');
                } else {
                    setErrorMessage(err.message);
                }
            } else {
                setErrorMessage('Unknown Error');
            }
        }).finally(() => {
            setLoading(false);
            setpopuploading(false);
        });
    };

    const onStockDataEdit = async (StockData) => {
        setErrorMessage("");
        const farmId = StockData.transferFarmID || StockData.farmId;
        const updatedSheds = await getAllSheds(farmId);
        currentStockData = {
            ...StockData,
            medicineStockTypeIdx: MaterialList[StockData.materialType].findIndex((material) => material.materialMasterId === StockData.materialMasterId),
            shedTypeIdx: updatedSheds.findIndex((shed) => shed.id === StockData.shedId),
            farmIndex: farms.findIndex((farm) => StockData.isTransfer ? farm.id === StockData.transferFarmID : farm.id === StockData.farmId),
            materialTypeIdx: MaterialTypeList.findIndex((el) => el.value === StockData.materialTypeId),
            isStorage: StockData.isStorage,
        };
        setMaterialType(MaterialTypeList[MaterialTypeList.findIndex((el) => el.value === StockData.materialTypeId)]);
        editMode = true;
        setShowStockDialog(true);
    };

    const onStockUpdate = (StockData, setPopupLoading) => {
        const response = api.post(`${stockApiFor(StockData)}/update/${StockData.id}/${match.params["farmID"]}`, StockData);
        response.then((res) => {
            setStockData(prev => prev.map(row => sameRow(row, StockData) ? { ...res } : row));
            setShowStockDialog(false);
        }).catch(err => {
            if (err.message) {
                if (err.message === 'Bad Request' || err.message === 'Not Updated') {
                    setErrorMessage('Stock not Updated');
                } else if (err.message === 'no batch found') {
                    setErrorMessage(`No batch found for shed ${StockData.shedName} on ${moment(StockData.date).format("DD-MM-YYYY")}`);
                } else if (err.message === 'no stock') {
                    setErrorMessage('Material with available stock 0 cannot be updated');
                } else {
                    setErrorMessage(err.message);
                }
            } else {
                setErrorMessage('Unknown Error');
            }
        }).finally(() => {
            setPopupLoading(false);
            setLoading(false);
        });
    };

    //Merged medicine + feed movement records
    const getAllStockData = () => {
        if (filterSelectedEndDate !== null && filterSelectedStartDate !== null &&
            filterSelectedEndDate < filterSelectedStartDate) {
            showError('Start Date is Greater than or Equal to End Date');
            return Promise.resolve();
        }
        setShowStockDialog(false);
        showError('');
        showMessage('');
        setLoading(true);

        const body = { date: filterSelectedStartDate, endDate: filterSelectedEndDate };
        const medicineReq = api.post(`medicine-stock/data/${match.params["farmID"]}`, body);
        const feedReq = api.post(`feed-stock/data/${match.params["farmID"]}`, body);

        return Promise.allSettled([medicineReq, feedReq]).then(([med, feed]) => {
            const medRows = med.status === 'fulfilled' && Array.isArray(med.value) ? med.value : [];
            const feedRows = feed.status === 'fulfilled' && Array.isArray(feed.value) ? feed.value : [];
            const merged = [...medRows, ...feedRows];
            setStockData(merged);
            if (merged.length === 0) showError('No Stock Found');
        }).finally(() => {
            setLoading(false);
        });
    };

    // Material types: Medicine + Vaccination (material-type) and Feed (feed list)
    const getMaterialTypes = () => {
        showError('');
        showMessage('');
        const medicineReq = api.get(`material-type`);
        const feedReq = api.post(`/feed-stock/get-feed-list/${match.params["farmID"]}`, {});

        return Promise.allSettled([medicineReq, feedReq]).then(([medRes, feedRes]) => {
            const materialTypes = [];
            const list = {};

            if (medRes.status === 'fulfilled' && medRes.value) {
                const res = medRes.value;
                if (res.Medicine) {
                    materialTypes.push({ key: 0, value: res.Medicine[0].materialTypeId, type: res.Medicine[0].type });
                    list.Medicine = res.Medicine;
                }
                if (res.Vaccination) {
                    materialTypes.push({ key: 1, value: res.Vaccination[0].materialTypeId, type: res.Vaccination[0].type });
                    list.Vaccination = res.Vaccination;
                }
            }

            const feedArr = feedRes.status === 'fulfilled' && feedRes.value && Array.isArray(feedRes.value.Feed)
                ? feedRes.value.Feed : [];
            materialTypes.push({ key: 2, value: FEED_TYPE_ID, type: 'Feed' });
            list.Feed = feedArr;

            setMaterialTypeList(materialTypes);
            setMaterialList(list);
        });
    };

    //Get all Shed API Call (on farm change inside dialog)
    const onGetShed = (farmId) => {
        setShed([]);
        showError('');
        const response = api.get(`farm/${farmId}/shed`);
        response.then(res => {
            setShed([...res]);
        }).catch(err => {
            if (err.message) {
                if (err.message === 'Bad Request' || err.message === 'No data found')
                    showError('No Sheds');
                else
                    showError(err.message);
            } else {
                showError('Unknown Error');
            }
        });
    };

    const onload = () => {
        setLoading(true);
        Promise.all([getAllStockData(), getAllFarms(), getAllSheds(match.params["farmID"]), getMaterialTypes()]).finally(() => {
            setLoading(false);
        });
    };

    const clearDateRangeFilter = () => {
        setFilterSelectedStartDate(null);
        setFilterSelectedEndDate(null);
    };

    useEffect(onload, []);

    let materialColumnDefaults = { title: 'Material Name', field: 'materialName', defaultSort: 'asc' };
    let materialColumn = {
        ...materialColumnDefaults,
        render: rowData => <Mobile data={rowData}
            onEdit={() => { onStockDataEdit(rowData); }}
            onDelete={() => { onStockDataDelete(rowData); }}
        />,
        // Other columns are hidden on mobile, and material-table skips hidden columns when searching
        customFilterAndSearch: (term, rowData) => {
            const values = [
                rowData.id,
                moment(rowData.date).format("DD-MM-YYYY"),
                rowData.materialName,
                rowData.materialType,
                rowData.shedName,
                rowData.quantity,
            ];
            return values.some(value => value !== undefined && value !== null &&
                String(value).toLowerCase().includes(term.toLowerCase()));
        },
    };
    let actions = [];
    if (isDesktop) {
        materialColumn = materialColumnDefaults;
        actions = [
            {
                icon: () => <Icon color='primary'>edit</Icon>,
                tooltip: 'Edit Stock',
                onClick: (event, rowData) => onStockDataEdit(rowData)
            },
            {
                icon: () => <Icon color='error'>delete</Icon>,
                tooltip: 'Delete Stock',
                onClick: (event, rowData) => onStockDataDelete(rowData)
            }
        ];
    }

    return <div className={classes.root}>
        <div className={classes.topBar}>
            <Typography variant="body1" color="textSecondary">
                Move stock to sheds or transfer between farms
            </Typography>
            <Button variant="contained" color="primary" size="small" endIcon={<AddCircleIcon />} onClick={onNewStock}>Move Stock</Button>
        </div>
        <div className="pp-filter-bar">
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                    autoOk
                    margin="none"
                    variant="inline"
                    inputVariant="outlined"
                    size="small"
                    label="From"
                    format="dd/MM/yyyy"
                    value={filterSelectedStartDate || null}
                    onChange={handleFilterStartDateChange}
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                />
                <KeyboardDatePicker
                    autoOk
                    margin="none"
                    variant="inline"
                    inputVariant="outlined"
                    size="small"
                    label="To"
                    format="dd/MM/yyyy"
                    value={filterSelectedEndDate || null}
                    onChange={handleFilterEndDateChange}
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                />
            </MuiPickersUtilsProvider>
            <div className="flex-gap-6">
                <Button onClick={getAllStockData} variant="contained" color="secondary" size="small" endIcon={<SearchIcon />}>Search</Button>
                <Button onClick={clearDateRangeFilter} variant="outlined" size="small" endIcon={<CloseIcon />}>Clear Search</Button>
            </div>
        </div>
        <MaterialTable
            isLoading={loading}
            columns={[
                { title: 'No', field: 'id', hidden: !isDesktop },
                {
                    title: 'Date', field: 'date', hidden: !isDesktop,
                    render: rowData => <span>{moment(rowData.date).format("DD-MM-YYYY")}</span>
                },
                materialColumn,
                { title: 'Material Type', field: 'materialType', hidden: !isDesktop, editable: 'never' },
                { title: 'Shed/Storage', field: 'shedName', hidden: !isDesktop, editable: 'never' },
                { title: 'Quantity', field: 'quantity', hidden: !isDesktop, editable: 'never' },
                {
                    title: 'Cost', field: 'cost', hidden: !isDesktop,
                    render: rowData => (rowData.cost != null ? rowData.cost.toFixed(2) : '')
                },
            ]}
            data={stockData}
            title='Stock Movement'
            options={{
                actionsColumnIndex: -1,
                search: true,
                showTitle: true,
                toolbar: true,
                header: isDesktop,
            }}
            actions={actions}
        />
        <MedicineStock errorMessage={errorMessage} showDialog={showStockDialog} handleClose={handleStockClose} onSave={onStockSave} onEdit={onStockUpdate}
            editMode={editMode} data={currentStockData} deleteErrorMessage={removeErrorMessage} match={match} shed={Shed} materialList={MaterialList}
            materialTypeList={MaterialTypeList} materialType={MaterialType}
            handleMaterialType={handleMaterialTypeChange} onGetShed={onGetShed} farms={farms}
        />
        <ConfirmDialog showDialog={showDeleteConfirm}
            handleClose={handleDeleteConfirmClose}
            onConfirm={onStockDeleteConfirm}
            title='Delete Stock ?'
            subText='This action will delete the stock movement record. Please confirm.' />
    </div>;
};

export default StockMovement;
